import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiExternalLink,
  FiZap,
  FiLayers,
  FiMail,
  FiFileText,
  FiSmartphone,
  FiRefreshCw,
  FiArrowRight,
} from 'react-icons/fi';
import SEO from '../../components/SEO';
import './CaseStudy.css';

const ACCENT = '#014D7F';
const LIVE_URL = 'https://asahealthmd.com/';
const IMG_MAC =
  'https://asahealthmd.s3.amazonaws.com/asahealthmdscreenhp-front.png';
const IMG_PHONE =
  'https://asahealthmd.s3.amazonaws.com/asahealthmdscreenhp-portrait.png';

const ASAHealthMDCaseStudy = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'ASA Health MD — Case Study',
    headline: 'Beauty · Brain · Bio — Full Rebrand + Site Rebuild',
    description:
      "How SilverFox Media rebranded and rebuilt ASA Health MD (formerly advsurgicalarts.com): a Next.js 14 + TypeScript + Tailwind marketing site with a Node/Express + MongoDB Atlas backend, SendGrid inquiry routing, and a three-pillar brand system (Aesthetics, Cosmetic Surgery, Wellness) — all deployed as a single Render web service.",
    image: IMG_MAC,
    url: 'https://silverfoxmedia.co/portfolio/asahealthmd',
    author: {
      '@type': 'Organization',
      name: 'SilverFox Media',
      url: 'https://silverfoxmedia.co',
    },
    about: [
      { '@type': 'Thing', name: 'Medical Aesthetics' },
      { '@type': 'Thing', name: 'Wellness' },
      { '@type': 'Thing', name: 'Next.js 14' },
      { '@type': 'Thing', name: 'TypeScript' },
      { '@type': 'Thing', name: 'Brand Rebuild' },
    ],
  };

  return (
    <>
      <SEO
        title="ASA Health MD Case Study: Beauty · Brain · Bio Rebrand + Site Rebuild"
        description="How SilverFox Media rebranded and rebuilt ASA Health MD (formerly advsurgicalarts.com): a Next.js 14 + TypeScript + Tailwind marketing site with a Node/Express + MongoDB Atlas backend, SendGrid inquiry routing, and a three-pillar brand system covering aesthetics, cosmetic surgery, and wellness."
        keywords="ASA Health MD case study, medical aesthetics rebrand, Plano med spa website, Next.js 14 case study, TypeScript Tailwind CSS, MongoDB Atlas, SendGrid, wellness clinic website, WordPress to Next.js migration"
        image="https://silverfoxmedia.co/images/og/asahealthmd.jpg"
        url="/portfolio/asahealthmd"
        type="article"
        schema={structuredData}
      />

      <article
        className="case-study"
        style={{ '--case-study-accent': ACCENT }}
      >
        {/* HERO — 2-col on desktop, media hidden on mobile */}
        <section className="case-study__hero">
          <div className="container">
            <div className="case-study__hero-grid">
              <div className="case-study__hero-inner">
                <Link to="/portfolio" className="case-study__back">
                  <FiArrowLeft />
                  Back to Portfolio
                </Link>

                <span className="case-study__category">Medical / Wellness</span>

                <h1 className="case-study__title">
                  ASA Health MD:{' '}
                  <span className="case-study__title-accent">
                    Beauty · Brain · Bio
                  </span>
                </h1>

                <p className="case-study__tagline">
                  A complete rebrand and site rebuild for a Plano, Texas medical
                  aesthetics and wellness practice — from a WordPress/Elementor
                  legacy site to a Next.js 14 + TypeScript stack with a
                  Node/Express + MongoDB Atlas backend and SendGrid-routed
                  inquiry intake. Three service pillars, one navigation, one
                  Render deploy.
                </p>

                <div className="case-study__hero-cta">
                  <a
                    href={LIVE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="case-study__cta-primary"
                  >
                    Visit Live Site <FiExternalLink />
                  </a>
                  <Link to="/contact" className="case-study__cta-secondary">
                    Start Your Project <FiArrowRight />
                  </Link>
                </div>

                <div className="case-study__hero-meta">
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Client</span>
                    <span className="case-study__hero-meta-value">ASA Health MD</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Industry</span>
                    <span className="case-study__hero-meta-value">Medical / Wellness</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Timeline</span>
                    <span className="case-study__hero-meta-value">2026 — Ongoing</span>
                  </div>
                  <div className="case-study__hero-meta-item">
                    <span className="case-study__hero-meta-label">Role</span>
                    <span className="case-study__hero-meta-value">Rebrand + Full Build</span>
                  </div>
                </div>
              </div>

              <div className="case-study__hero-media">
                <img
                  src={IMG_MAC}
                  alt="ASA Health MD homepage on a MacBook mockup"
                  className="case-study__hero-macbook"
                  loading="lazy"
                />
                <img
                  src={IMG_PHONE}
                  alt="ASA Health MD hero on a portrait mobile screen"
                  className="case-study__hero-phone"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* FEATURED IMAGE — mobile only */}
        <section className="case-study__featured">
          <div className="container">
            <div className="case-study__featured-image-wrap">
              <img
                src={IMG_MAC}
                alt="ASA Health MD homepage on a MacBook mockup"
                className="case-study__featured-image"
                loading="lazy"
              />
              <img
                src={IMG_PHONE}
                alt="ASA Health MD hero on a portrait mobile screen"
                className="case-study__featured-phone"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* OVERVIEW */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Overview</p>
            <h2 className="case-study__section-title">
              One practice, three service pillars, a new name.
            </h2>
            <div className="case-study__prose">
              <p>
                ASA Health MD (previously Advanced Surgical Arts) is a Plano,
                Texas practice with three service pillars — Aesthetics (MedSpa),
                Cosmetic Surgery, and Wellness. The old site
                (advsurgicalarts.com) was a WordPress / Elementor build focused
                heavily on cosmetic surgery, which left the aesthetics and
                wellness verticals underrepresented and made it hard for
                prospective patients to understand what the practice actually
                offered.
              </p>
              <p>
                The engagement covered the full arc: new brand name, new
                identity system (Beauty · Brain · Bio), new domain, and a
                ground-up site rebuild on a modern stack. The goal was a site
                that reads clearly across all three service categories without
                any single vertical dominating navigation, with inquiry flows
                that route directly to the practice's team via SendGrid — no
                intermediate CRM.
              </p>
            </div>
          </div>
        </section>

        {/* CHALLENGE */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Challenge</p>
            <h2 className="case-study__section-title">
              Three verticals, one navigation, without losing the surgery brand equity.
            </h2>
            <div className="case-study__prose">
              <p>
                The practice's existing brand equity was concentrated in
                cosmetic surgery, but the growth verticals were aesthetics and
                wellness. Rebranding aggressively risked losing surgery search
                traffic and long-tenured patient recognition; rebranding
                conservatively risked another five years of the same
                surgery-heavy positioning that led to the rebuild in the first
                place.
              </p>
              <p>
                The Beauty · Brain · Bio system solves this by giving each
                pillar equal navigation weight and a distinct semantic color
                token from the brand system (Teal for Aesthetics, Cobalt for
                Cosmetic Surgery, Green for Wellness) — surgery keeps its
                center-of-gravity but no longer dominates.
              </p>
              <p>
                The tech side had its own constraint: the practice wanted a
                fast, editable site without a CMS install in Phase 1. Content
                lives in typed React components; Sanity.io moves in during
                Phase 2 once the content team is ready. Everything else runs
                on a single Render web service for simplicity.
              </p>
            </div>
          </div>
        </section>

        {/* SOLUTION / KEY FEATURES */}
        <section className="case-study__section">
          <div className="container">
            <p className="case-study__section-label">The Solution</p>
            <h2 className="case-study__section-title">
              Next.js 14, TypeScript throughout, one Render service.
            </h2>
            <p className="case-study__section-lead">
              A Next.js 14 App Router frontend + Tailwind design system + a
              typed Node/Express backend + MongoDB Atlas, all deployed as one
              Render web service via concurrently. Sanity.io is architected in
              but not shipped until the content team is ready to own it.
            </p>

            <div className="case-study__features">
              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiRefreshCw /></span>
                <h3 className="case-study__feature-title">Full Rebrand</h3>
                <p className="case-study__feature-body">
                  New name, new domain, new brand system. Beauty · Brain · Bio
                  gives the three service categories equal weight without
                  abandoning the cosmetic-surgery search history that
                  advsurgicalarts.com had accumulated.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiZap /></span>
                <h3 className="case-study__feature-title">Next.js 14 App Router</h3>
                <p className="case-study__feature-body">
                  Server components for content-heavy service pages, client
                  components for interactive elements like consultation
                  request modals. TypeScript-first throughout, Tailwind CSS
                  design tokens keyed to the brand color system.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiLayers /></span>
                <h3 className="case-study__feature-title">Three-Pillar Design System</h3>
                <p className="case-study__feature-body">
                  Semantic color tokens per pillar — Teal for Aesthetics, Cobalt
                  for Cosmetic Surgery, Green for Wellness — with shared
                  typography (Montserrat + Georgia). Every service page inherits
                  its pillar's accent without theme fragmentation.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiMail /></span>
                <h3 className="case-study__feature-title">SendGrid Inquiry Routing</h3>
                <p className="case-study__feature-body">
                  Consultation-request and general inquiry forms route directly
                  to the practice via SendGrid — no middleware CRM, no third-
                  party form processor. The practice owns the contact list
                  outright and the audit trail is simple.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiFileText /></span>
                <h3 className="case-study__feature-title">Typed Backend</h3>
                <p className="case-study__feature-body">
                  Node.js + Express + TypeScript with Mongoose against MongoDB
                  Atlas. Every model, every request handler, and every response
                  is typed end-to-end — easier to audit, easier to onboard
                  future engineers.
                </p>
              </div>

              <div className="case-study__feature">
                <span className="case-study__feature-icon"><FiSmartphone /></span>
                <h3 className="case-study__feature-title">Mobile-First Marketing Site</h3>
                <p className="case-study__feature-body">
                  Aesthetic and wellness patients research on their phones.
                  Every service page, consultation form, and hero was designed
                  mobile-first with touch-optimized interactions and safe-area
                  handling for iOS notches.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TECH STACK */}
        <section className="case-study__section" style={{ background: 'var(--color-gray-100)' }}>
          <div className="container">
            <p className="case-study__section-label">The Stack</p>
            <h2 className="case-study__section-title">Next.js 14 + TypeScript, top to bottom.</h2>
            <p className="case-study__section-lead">
              One deploy, one runtime, one language contract from the browser
              to the database.
            </p>

            <div className="case-study__tech">
              <span className="case-study__tech-chip">Next.js 14</span>
              <span className="case-study__tech-chip">TypeScript</span>
              <span className="case-study__tech-chip">Tailwind CSS</span>
              <span className="case-study__tech-chip">React 18</span>
              <span className="case-study__tech-chip">Node.js</span>
              <span className="case-study__tech-chip">Express</span>
              <span className="case-study__tech-chip">MongoDB Atlas</span>
              <span className="case-study__tech-chip">Mongoose</span>
              <span className="case-study__tech-chip">SendGrid</span>
              <span className="case-study__tech-chip">concurrently</span>
              <span className="case-study__tech-chip">Render</span>
              <span className="case-study__tech-chip">Sanity.io (Phase 2)</span>
            </div>
          </div>
        </section>

        {/* RESULTS */}
        <section className="case-study__section case-study__section--dark">
          <div className="container">
            <p className="case-study__section-label">The Results</p>
            <h2 className="case-study__section-title">Rebranded, rebuilt, and shipping.</h2>
            <p className="case-study__section-lead" style={{ color: 'rgba(255,255,255,0.75)' }}>
              ASA Health MD is live at asahealthmd.com — a new brand, a new
              stack, and a navigation that finally reflects everything the
              practice offers.
            </p>

            <div className="case-study__stats">
              <div className="case-study__stat">
                <p className="case-study__stat-value">3 pillars</p>
                <p className="case-study__stat-label">Aesthetics · Cosmetic Surgery · Wellness, equal navigation weight</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">100% typed</p>
                <p className="case-study__stat-label">TypeScript end-to-end, browser through database</p>
              </div>
              <div className="case-study__stat">
                <p className="case-study__stat-value">1 deploy</p>
                <p className="case-study__stat-label">Single Render web service — client + server via concurrently</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="case-study__section">
          <div className="container">
            <div className="case-study__cta">
              <h2 className="case-study__cta-title">Rebranding an established practice?</h2>
              <p className="case-study__cta-text">
                Legacy WordPress migrations, modern stack rebuilds, brand
                systems that reflect what you actually offer — we handle
                rebrands and site rebuilds like ASA Health MD end-to-end.
              </p>
              <Link to="/contact" className="case-study__cta-btn">
                Start Your Project <FiArrowRight />
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default ASAHealthMDCaseStudy;
